// app/src/lib/scale/integrations.js — QuickBooks Online + Xero exports (US-5.6, US-5.7).
//
// Client surface is intentionally thin: OAuth-token storage lives in AWS Secrets
// Manager on the server side; this module exposes:
//   - `startOauth(provider)` → redirect URL from Lambda.
//   - `completeOauth(provider, code, state)` → server exchanges + stashes tokens.
//   - `push(provider, opts)` → Lambda iterates Kanjo records, pushes to the
//     external accounting system, and persists external IDs to dedupe future runs.
//
// External IDs (the records' QBO/Xero entity IDs) are stored on the source rows
// via per-provider columns (e.g. `qboBillId`, `xeroBillId`) so re-running export
// is idempotent.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';
import { gate } from './members.js';
import * as audit from './audit.js';

const VALID_PROVIDERS = new Set(['qbo', 'xero']);

/** Begin OAuth: server returns the provider's authorization URL with our state token. */
export async function startOauth(provider) {
  if (!VALID_PROVIDERS.has(provider)) throw new Error('integration: invalid provider');
  const claims = await currentClaims();
  if (!claims) throw new Error('integration: not signed in');
  await gate(claims.sub, 'integration.manage');
  const cfg = getConfig();
  const at = await getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}/integrations/${provider}/start`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}` },
  });
  if (!res.ok) throw new Error(`integration: start ${res.status}`);
  const body = await res.json();
  if (!body.authorizationUrl) throw new Error('integration: malformed start response');
  return body.authorizationUrl;
}

/** OAuth callback: server exchanges code → tokens (in Secrets Manager) and returns connection metadata. */
export async function completeOauth(provider, code, state) {
  if (!VALID_PROVIDERS.has(provider)) throw new Error('integration: invalid provider');
  const cfg = getConfig();
  const at = await getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}/integrations/${provider}/complete`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) throw new Error(`integration: complete ${res.status}`);
  const body = await res.json();
  const integrationId = body.integrationId;
  const row = {
    integrationId,
    ownerId: (await currentClaims()).sub,
    provider,
    status: 'connected',
    connectedAt: Date.now(),
    realmId: body.realmId,
    scopes: body.scopes,
  };
  await db.integrations.put(row);
  await audit.record({ action: 'create', entityType: 'integration', entityId: integrationId, after: { provider, status: 'connected' } });
  return row;
}

/** Trigger an export run server-side. Records pushed and any errors return in the response. */
export async function push(provider, opts = {}) {
  if (!VALID_PROVIDERS.has(provider)) throw new Error('integration: invalid provider');
  const claims = await currentClaims();
  if (!claims) throw new Error('integration: not signed in');
  await gate(claims.sub, 'integration.manage');
  const cfg = getConfig();
  const at = await getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}/integrations/${provider}/push`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({
      since: opts.since,
      kinds: opts.kinds || ['invoice', 'purchaseOrder', 'salesEntry'],
    }),
  });
  if (!res.ok) throw new Error(`integration: push ${res.status}`);
  const body = await res.json();
  await audit.record({
    action: 'export',
    entityType: 'integration',
    entityId: provider,
    meta: { exported: body.exported, errors: body.errors },
  });
  return body;
}

export async function listConnections() {
  const claims = await currentClaims();
  if (!claims) return [];
  return db.integrations.where('ownerId').equals(claims.sub).toArray();
}

export async function disconnect(integrationId) {
  const claims = await currentClaims();
  if (!claims) throw new Error('integration: not signed in');
  await gate(claims.sub, 'integration.manage');
  const integ = await db.integrations.get(integrationId);
  if (!integ || integ.ownerId !== claims.sub) throw new Error('integration: not found');
  const cfg = getConfig();
  const at = await getAccessToken();
  await fetch(`${cfg.apiBaseUrl}/integrations/${integ.provider}/disconnect`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ integrationId }),
  });
  await db.integrations.delete(integrationId);
  await audit.record({ action: 'delete', entityType: 'integration', entityId: integrationId });
}
